import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import Layout from './Layout';
import useCompanyContext from '../hooks/useCompanyContext';
import accountingDashboardAPI from '../api/accountingDashboardAPI';
import {
  DocumentTextIcon,
  Square3Stack3DIcon,
  ChartBarIcon,
  CalculatorIcon,
  BanknotesIcon,
  TableCellsIcon,
  ClipboardDocumentListIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

// ── Formatting helpers ──────────────────────────────────────────────────────
const formatPHP = (n) =>
  new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

const formatAxis = (v) =>
  `₱${Number(v).toLocaleString('en-PH', { notation: 'compact', maximumFractionDigits: 1 })}`;

// ── Chart colours ───────────────────────────────────────────────────────────
const C_REVENUE  = '#0d9488'; // teal-600
const C_EXPENSE  = '#d1d5db'; // gray-300
const C_POSITIVE = '#14b8a6'; // teal-500
const C_NEGATIVE = '#ef4444'; // red-500

// ── Custom tooltip for Revenue/Expense chart ────────────────────────────────
const RevExpTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-sm min-w-[180px]">
      <p className="font-semibold text-gray-900 mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.fill }} className="font-medium">{p.name}</span>
          <span className="font-medium text-gray-800">{formatPHP(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Custom tooltip for Net Income chart ────────────────────────────────────
const NetIncomeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-sm min-w-[160px]">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-gray-500">Net Income</span>
        <span className={`font-bold ${val >= 0 ? 'text-teal-700' : 'text-red-600'}`}>
          {formatPHP(val)}
        </span>
      </div>
    </div>
  );
};

// ── Report card data ────────────────────────────────────────────────────────
const REPORT_CARDS = [
  { id: 'trial-balance',    title: 'Trial Balance',         description: 'Debit and credit balances for all accounts',   icon: DocumentTextIcon },
  { id: 'balance-sheet',    title: 'Balance Sheet',         description: 'Assets, liabilities and equity snapshot',      icon: Square3Stack3DIcon },
  { id: 'income-statement', title: 'Income Statement',      description: 'Revenue, expenses and net income comparison',   icon: ChartBarIcon },
  { id: 'general-ledger',   title: 'General Ledger',        description: 'All journal entries by account',                icon: CalculatorIcon },
  { id: 'cash-flow',        title: 'Cash Flow Statement',   description: 'Operating, investing and financing flows',      icon: BanknotesIcon },
  { id: 'worksheet',        title: 'Worksheet',             description: 'Adjusting entries and FS columns',              icon: TableCellsIcon },
  { id: 'vat-report',       title: 'VAT Report',            description: 'Output and input VAT summary per period',       icon: ClipboardDocumentListIcon },
  { id: 'ar-aging',         title: 'AR Aging',              description: 'Accounts receivable aging analysis',            icon: ArrowTrendingUpIcon },
  { id: 'ap-aging',         title: 'AP Aging',              description: 'Accounts payable aging analysis',               icon: ArrowTrendingDownIcon },
];

// ── KPI skeleton card ───────────────────────────────────────────────────────
const KpiSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 animate-pulse">
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
    <div className="h-3 bg-gray-100 rounded w-1/3" />
  </div>
);

const ChartSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-40 mb-6" />
    <div className="h-56 bg-gray-100 rounded" />
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────
const AccountingFirmDashboard = () => {
  const navigate = useNavigate();
  
  // ✅ NEW: Use hook instead of useMemo with empty deps
  // This will update when company switches in header
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();

  // Reactive: updates when contextAccountingClientId changes (via company switch)
  const accountingClientId = contextAccountingClientId || '';

  const fiscalYear = useMemo(() => {
    if (!accountingClientId) return new Date().getFullYear();
    const saved = parseInt(localStorage.getItem(`activeFiscalYear:${accountingClientId}`), 10);
    return !isNaN(saved) ? saved : new Date().getFullYear();
  }, [accountingClientId]);

  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!accountingClientId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await accountingDashboardAPI.getDashboardSummary({
          accountingClient: accountingClientId,
          fiscalYear,
        });
        if (!cancelled) setSummary(res.data || null);
      } catch (err) {
        if (!cancelled) setError(typeof err === 'string' ? err : err?.message || 'Failed to load dashboard data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [accountingClientId, fiscalYear]);

  // FY totals (sum across all 12 months)
  const fyNetIncome = summary?.monthlyIncome?.reduce((acc, m) => acc + (m.netIncome ?? 0), 0) ?? 0;

  // Has any monthly data at all?
  const hasMonthlyData = summary?.monthlyIncome?.some(m => m.revenue > 0 || m.expenses > 0);
  const hasNegativeNet = summary?.monthlyIncome?.some(m => m.netIncome < 0);

  const goTo = (reportId) => navigate(`/accounting-reports?report=${reportId}`);

  // ── KPI card definitions (values straight from backend) ───────────────────
  const kpis = summary
    ? [
        {
          label:    'Net Income',
          sub:      `FY ${fiscalYear}`,
          value:    formatPHP(fyNetIncome),
          negative: fyNetIncome < 0,
        },
        {
          label: 'Total Assets',
          sub:   `FY ${fiscalYear}`,
          value: formatPHP(summary.balanceSnapshot.assets),
        },
        {
          label: 'Total Liabilities',
          sub:   `FY ${fiscalYear}`,
          value: formatPHP(summary.balanceSnapshot.liabilities),
        },
        {
          label: 'AR Outstanding',
          sub:   'Accounts receivable',
          value: formatPHP(summary.arOutstanding),
        },
        {
          label: 'AP Outstanding',
          sub:   'Accounts payable',
          value: formatPHP(summary.apOutstanding),
        },
      ]
    : [];

  return (
    <Layout pageTitle="Dashboard">
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* ── Header ───────────────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-1">FY {fiscalYear}</p>
            </div>
          </div>

          {/* ── Error ────────────────────────────────────────────── */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-900 font-medium text-sm">{error}</p>
            </div>
          )}

          {/* ── No client selected ───────────────────────────────── */}
          {!accountingClientId && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-yellow-900 font-medium">No accounting client selected.</p>
              <p className="text-yellow-700 text-sm mt-1">
                Select an accounting client from the sidebar to view financial data.
              </p>
            </div>
          )}

          {/* ── KPI Cards ────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)
              : kpis.map(({ label, sub, value, negative }) => (
                <div
                  key={label}
                  className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm"
                >
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {label}
                  </p>
                  <p className={`text-xl font-bold break-all ${negative ? 'text-red-600' : 'text-teal-700'}`}>
                    {value}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{sub}</p>
                </div>
              ))
            }
          </div>

          {/* ── Charts ───────────────────────────────────────────── */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
          ) : summary && hasMonthlyData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Revenue vs Expenses grouped bar */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Revenue vs Expenses</h3>
                  <p className="text-xs text-gray-400">FY {fiscalYear} — by month</p>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={summary.monthlyIncome} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis tickFormatter={formatAxis} tick={{ fill: '#6b7280', fontSize: 10 }} width={70} />
                    <Tooltip content={<RevExpTooltip />} />
                    <Legend
                      formatter={(value) => (
                        <span className="text-xs text-gray-600 capitalize">{value}</span>
                      )}
                    />
                    <Bar dataKey="revenue"  name="Revenue"  fill={C_REVENUE}  radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="expenses" name="Expenses" fill={C_EXPENSE}  radius={[3, 3, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Net Income per month */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Net Income Trend</h3>
                  <p className="text-xs text-gray-400">FY {fiscalYear} — revenue minus expenses</p>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={summary.monthlyIncome} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis tickFormatter={formatAxis} tick={{ fill: '#6b7280', fontSize: 10 }} width={70} />
                    {hasNegativeNet && <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 2" />}
                    <Tooltip content={<NetIncomeTooltip />} />
                    <Bar dataKey="netIncome" name="Net Income" radius={[3, 3, 0, 0]} maxBarSize={40}>
                      {summary.monthlyIncome.map((entry, i) => (
                        <Cell
                          key={`ni-${i}`}
                          fill={entry.netIncome >= 0 ? C_POSITIVE : C_NEGATIVE}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-sm bg-teal-500" /> Profit
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-sm bg-red-500" /> Loss
                  </span>
                </div>
              </div>

            </div>
          ) : summary && !hasMonthlyData && !loading ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-400 text-sm">No general ledger entries found for FY {fiscalYear}.</p>
            </div>
          ) : null}

          {/* ── Report Cards ─────────────────────────────────────── */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounting Reports</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {REPORT_CARDS.map(({ id, title, description, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => goTo(id)}
                  className="text-left bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm
                             hover:shadow-md hover:border-teal-400 hover:bg-teal-100
                             transition-all focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <Icon className="h-9 w-9 text-teal-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                  <p className="text-sm text-gray-600">{description}</p>
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default AccountingFirmDashboard;
